import { useEffect, useState } from "react";
import { Loader2, Download, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { staffApi, fmtErr } from "../api";

const fmtTs = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
  } catch {
    return iso;
  }
};

// Admin → Audit Logs: read-only trail from the central system (+ CSV export generated server-side).
// Friendly labels for known actions (attendance/geofence/admin); unknown actions pass through raw.
const ACTION_LABELS = {
  attendance_event_correct: "Attendance correction",
  attendance_event_corrected: "Attendance correction",
  attendance_event_delete: "Invalid session deleted",
  attendance_event_deleted: "Invalid session deleted",
  attendance_event_add: "Missed session added",
  attendance_event_added: "Missed session added",
  attendance_event_create: "Missed session added",
  branch_update: "Property/geofence updated",
  branch_updated: "Property/geofence updated",
  branch_create: "Property created",
  branch_delete: "Property deleted",
  user_branch_update: "Employee property assignment updated",
  user_update: "Employee record updated",
  office_config_update: "Auto-attendance settings updated",
  config_office_update: "Auto-attendance settings updated",
  config_update: "Auto-attendance settings updated",
  permission_grant: "Permission granted",
  permission_revoke: "Permission revoked",
  role_update: "Role updated",
  user_create: "Employee created",
  user_invite: "Onboarding invite sent",
  onboarding_token: "Onboarding link generated",
};

const AuditLogs = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [action, setAction] = useState("");
  const [limit, setLimit] = useState("100");
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const params = { limit: parseInt(limit, 10) || 100 };
      if (action.trim()) params.action = action.trim();
      const { data } = await staffApi.get("/audit-logs", { params });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await staffApi.get("/audit-logs.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Audit log CSV downloaded.");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setDownloading(false);
    }
  };

  const selectCls = "border border-slate-300 px-3 py-2 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]";

  return (
    <div data-testid="admin-audit-logs">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Audit Logs</h1>
          <p className="mt-1 text-sm text-slate-500">Every sensitive action in the central system, newest first. Read-only.</p>
        </div>
        <button onClick={downloadCsv} disabled={downloading} data-testid="audit-download-btn"
          className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-[#C5221F] hover:text-[#C5221F] disabled:opacity-50 text-slate-700 px-4 py-2 text-[11px] font-display font-semibold uppercase tracking-widest rounded-sm transition-colors">
          {downloading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Download CSV
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <input type="text" placeholder="Filter by action (e.g. user_update)" value={action} data-testid="audit-filter-action"
          onChange={(e) => setAction(e.target.value)}
          className="border border-slate-300 px-3.5 py-2 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F] w-64" />
        <select value={limit} onChange={(e) => setLimit(e.target.value)} data-testid="audit-limit" className={selectCls}>
          {["50", "100", "250", "500"].map((l) => <option key={l} value={l}>Last {l}</option>)}
        </select>
        <button onClick={load} data-testid="audit-apply-btn"
          className="bg-[#0A1128] hover:bg-[#16204a] text-white font-display font-semibold px-5 py-2 text-xs uppercase tracking-widest rounded-sm transition-colors">
          Apply
        </button>
      </div>

      {loading ? (
        <p className="mt-10 flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</p>
      ) : error ? (
        <p className="mt-6 text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="audit-error">{error}</p>
      ) : rows.length === 0 ? (
        <p className="mt-6 text-sm text-slate-500 bg-white border border-slate-200 p-6" data-testid="audit-empty">No audit entries found.</p>
      ) : (
        <div className="mt-5 bg-white border border-slate-200 overflow-x-auto" data-testid="audit-table">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                {["Time", "Actor", "Action", "Target", "Reason"].map((h) => (
                  <th key={h} className="px-4 py-3 font-mono-tech text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`audit-row-${r.id}`}>
                  <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtTs(r.timestamp)}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-800 flex items-center gap-1.5"><ScrollText size={12} className="text-slate-300" />{r.actor_name}</p>
                    <p className="text-xs text-slate-400">{r.actor_role}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-block bg-slate-100 text-slate-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider" title={r.action}>{ACTION_LABELS[r.action] || r.action}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {r.target_type}{r.target_name ? ` — ${r.target_name}` : ""}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{r.reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
